import { formatUsd } from '../../../lib/format';
import { CARBON_ABATEMENT_CAPTION } from './carbonCopy';
import { formatTonnes } from './carbonCategories';

/**
 * SIL-440 — one ranked abatement row (spec §1 "$/tCO2e abatement ranking",
 * copy §11).
 *
 * `deltaTco2e` is scenario minus baseline, so a REDUCTION is negative — the
 * same sign convention the compare response's `delta` block uses, kept
 * end-to-end rather than flipped for display, so a reader comparing this
 * table against the exported tidy CSV never has to reconcile two conventions.
 *
 * `usdPerTco2e` is null whenever the row is not rankable, and
 * `notRankableReason` then says WHY in prose. There are two distinct
 * not-rankable cases and they must never collapse into one dash:
 *   - the action saves money while abating (a negative cost per tonne is not
 *     a "price" — ranking it numerically against positive prices reads as
 *     "expensive"), and
 *   - the action does not abate at all (dividing by a non-reduction produces
 *     a meaningless or sign-flipped figure).
 */
export interface AbatementRow {
  action: string;
  deltaTco2e: number;
  deltaUsd: number;
  usdPerTco2e: number | null;
  notRankableReason: string | null;
}

/**
 * Builds one ranked row from a footprint delta and a net-position delta.
 *
 * `deltaNetPositionUsd` is scenario-minus-baseline NET POSITION, where higher
 * is better for the operator; the abatement COST is therefore its negation.
 */
export function buildAbatementRow(params: {
  action: string;
  deltaTco2e: number;
  deltaNetPositionUsd: number;
}): AbatementRow {
  const { action, deltaTco2e, deltaNetPositionUsd } = params;
  const abatedTonnes = -deltaTco2e;
  const costUsd = -deltaNetPositionUsd;

  if (abatedTonnes <= 0) {
    return {
      action,
      deltaTco2e,
      deltaUsd: deltaNetPositionUsd,
      usdPerTco2e: null,
      notRankableReason: 'Does not reduce the forecast footprint — no abatement to price.',
    };
  }
  if (costUsd <= 0) {
    return {
      action,
      deltaTco2e,
      deltaUsd: deltaNetPositionUsd,
      usdPerTco2e: null,
      notRankableReason: 'Saves money while reducing the footprint — no cost per tonne to rank.',
    };
  }
  return {
    action,
    deltaTco2e,
    deltaUsd: deltaNetPositionUsd,
    usdPerTco2e: costUsd / abatedTonnes,
    notRankableReason: null,
  };
}

/** Cheapest abatement first: money-saving reductions, then ascending $/tCO2e,
 * then everything that abates nothing. */
function rankOrder(row: AbatementRow): [number, number] {
  if (row.usdPerTco2e !== null) return [1, row.usdPerTco2e];
  if (row.deltaTco2e < 0) return [0, 0]; // saves money AND abates
  return [2, 0];
}

export function AbatementRankingTable({
  rows,
  /** Prose disclosure of what this table CANNOT yet rank, rendered under the
   * §11 caption. Never omitted silently when a grain is missing. */
  gapNote,
}: {
  rows: AbatementRow[];
  gapNote?: string;
}) {
  const sorted = [...rows].sort((a, b) => {
    const [ga, va] = rankOrder(a);
    const [gb, vb] = rankOrder(b);
    return ga === gb ? va - vb : ga - gb;
  });

  return (
    <div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-violet-200 text-left text-xs uppercase tracking-widest text-violet-700">
            <th className="py-1.5 font-normal">Action</th>
            <th className="py-1.5 text-right font-normal">Δ footprint</th>
            <th className="py-1.5 text-right font-normal">Δ net position</th>
            <th className="py-1.5 text-right font-normal">$/tCO2e</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.action} className="border-b border-violet-100 last:border-b-0">
              <td className="py-2 pr-3 align-top text-zinc-700">{row.action}</td>
              <td className="py-2 text-right align-top font-mono tabular-nums text-violet-800">
                {formatTonnes(row.deltaTco2e)}
              </td>
              <td className="py-2 text-right align-top font-mono tabular-nums text-zinc-600">
                {row.deltaUsd >= 0
                  ? `+${formatUsd(row.deltaUsd)}`
                  : `−${formatUsd(Math.abs(row.deltaUsd))}`}
              </td>
              <td className="py-2 pl-3 text-right align-top">
                {row.usdPerTco2e !== null ? (
                  <span className="font-mono tabular-nums text-zinc-800">
                    {formatUsd(row.usdPerTco2e)}
                  </span>
                ) : (
                  <span className="italic text-zinc-500">{row.notRankableReason}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-xs leading-relaxed text-violet-700">{CARBON_ABATEMENT_CAPTION}</p>
      {gapNote && <p className="mt-1 text-xs italic leading-relaxed text-amber-700">{gapNote}</p>}
    </div>
  );
}
